import { useMemo } from 'react'
import { Card, Typography, Space, Alert, Spin } from 'antd'
import { Filters } from './components/Filters'
import { WebhookLogsTable } from './components/WebhookLogsTable'
import { useWebhookLogs } from './hooks'
import { useWebhookLogsStore } from './store'
import type { WebhookLogsFilters } from './types'

const { Title } = Typography

export function WebhookLogsPage() {
  const [state, dispatch] = useWebhookLogsStore()

  const apiFilters: WebhookLogsFilters = useMemo(() => {
    const filters: WebhookLogsFilters = {
      page: state.pagination.currentPage - 1, // API uses 0-based pagination
      'page-size': state.pagination.pageSize
    }

    // Date range filters
    if (state.filters.dateRange.from) {
      filters['date-from'] = state.filters.dateRange.from
    }
    if (state.filters.dateRange.to) {
      filters['date-to'] = state.filters.dateRange.to
    }

    // Search query (covers EOB number, filename, and URL)
    if (state.searchQuery) {
      filters.search = state.searchQuery
    }

    // Column filters
    if (state.columnFilters.entity && state.columnFilters.entity.length > 0) {
      filters.entity = state.columnFilters.entity
    }
    if (state.columnFilters.responseStatus && state.columnFilters.responseStatus.length > 0) {
      filters['response-status'] = state.columnFilters.responseStatus
    }
    if (state.columnFilters.tokenValid && state.columnFilters.tokenValid.length > 0) {
      filters['token-valid'] = state.columnFilters.tokenValid
    }
    if (state.columnFilters.isOnHold && state.columnFilters.isOnHold.length > 0) {
      filters['is-on-hold'] = state.columnFilters.isOnHold
    }

    return filters
  }, [state])

  const { data, isLoading, error, isError } = useWebhookLogs(apiFilters)

  const handleDateRangeChange = (dateRange: { from?: string; to?: string }) => {
    dispatch({ type: 'filters/dateRange/set', payload: dateRange })
  }

  const handleSearchChange = (query: string | undefined) => {
    dispatch({ type: 'searchQuery/set', payload: query })
  }

  const handleColumnFiltersChange = (columnFilters: typeof state.columnFilters) => {
    dispatch({ type: 'columnFilters/set', payload: columnFilters })
  }

  const handleFiltersClear = () => {
    dispatch({ type: 'filters/clear' })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    if (pageSize !== state.pagination.pageSize) {
      dispatch({ type: 'pagination/pageSize/set', payload: pageSize })
    } else {
      dispatch({ type: 'pagination/currentPage/set', payload: page })
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction='vertical' style={{ width: '100%' }} size='large'>
        <div>
          <Title level={2}>Webhook Logs</Title>
          <Typography.Text type='secondary'>View and analyze webhook call logs</Typography.Text>
        </div>

        <Filters
          dateRange={state.filters.dateRange}
          searchQuery={state.searchQuery}
          onDateRangeChange={handleDateRangeChange}
          onSearchChange={handleSearchChange}
          onClear={handleFiltersClear}
          loading={isLoading}
        />

        {isError && (
          <Alert
            message='Data Loading Error'
            description={error?.message || 'An unknown error occurred'}
            type='error'
            showIcon
          />
        )}

        <Card>
          {isLoading && !data ? (
            <div style={{ textAlign: 'center', padding: 50 }}>
              <Spin size='large' />
              <div style={{ marginTop: 16 }}>Loading data...</div>
            </div>
          ) : (
            <WebhookLogsTable
              data={data?.data || []}
              loading={isLoading}
              pagination={{
                current: state.pagination.currentPage,
                pageSize: state.pagination.pageSize,
                total: data?.pagination.total || 0
              }}
              columnFilters={state.columnFilters}
              onPaginationChange={handlePaginationChange}
              onColumnFiltersChange={handleColumnFiltersChange}
            />
          )}
        </Card>

        {data && (
          <div style={{ textAlign: 'center', color: '#666' }}>
            <Typography.Text type='secondary'>
              Showing {data.data.length} of {data.pagination.total} records
              {data.pagination.totalPages > 1 && (
                <>
                  {' '}
                  • Page {data.pagination.page + 1} of {data.pagination.totalPages}
                </>
              )}
            </Typography.Text>
          </div>
        )}
      </Space>
    </div>
  )
}
